import React from 'react'
import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useParams } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import axiosInstance from '../axios.config';
import { LeftSideBlock } from '../components/CourseDetails/LeftSideBlock';
import { AddCourseStepModal } from '../components/CourseDetails/AddCourseStepModal';
import { UpdateCourseStepModal } from '../components/CourseDetails/UpdateCourseStepModal';
import { useCourseDetailsHandlers } from '../components/CourseDetails/useCourseDetailsHandlers';
import { Box, Button, Typography } from '@mui/material';
import { ContentBlock } from '../shared/sharedStyles';
import { RightSideBlock } from '../components/CourseDetails/RightSideBlock';
import { useEditCourse } from '../components/CourseDetails/utils/useEditCourse';
import { useAddCourse } from '../components/CourseDetails/utils/useAddCourse';

const CourseDetails = () => {
  // Memoize lngsltd to prevent infinite loops
  const lngsltd = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('languageSelected')) || {};
    } catch (error) {
      console.error('Error parsing language data:', error);
      return {};
    }
  }, []);

  const { id: courseId } = useParams();
  const [courseSteps, setCourseSteps] = useState([]);
  const [stepContentState, setStepContentState] = useState({
    activeContentType: "",
    ContentType: "",
    englishDescriptionContent: "",
    norwegianDescriptionContent: "",
    imageFiles: [],
    imageUrls: [],
    imageFileNames: [],
    videoFiles: [],
    videoUrls: [],
    descriptionBgImgFile: null,
    hasNewFiles: false  // Track if files are newly uploaded
  })
  const [selectedStep, setSelectedStep] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const mediaInputRef = useRef(null)

  // Memoize fetchCourseSteps to prevent unnecessary re-renders
  const fetchCourseSteps = useCallback(async () => {
    try {
      const response = await axiosInstance.get(`/CourseStep/Course/${courseId}`);
      //Steps in ascending order
      const sortedData = response.data.slice().sort((a, b) => a.stepNo - b.stepNo);
      setCourseSteps(sortedData);
      return sortedData;
    }
    catch (error) {
      console.error('Error fetching course steps:', error);
      return [];
    }
  }, [courseId]);

  const {
    showedit,
    editStepTitle,
    handleCloseEdit,
    setEditStepTitle,
    handleEdit,
    handleUpdate
  } = useEditCourse(lngsltd, fetchCourseSteps)

  const {
    showadd,
    stepNumberError,
    newStepData,
    handleStepNumberChange,
    handleSave,
    handleShowAdd,
    handleCloseAdd,
    setNewStepData
  } = useAddCourse(courseId, lngsltd, fetchCourseSteps)

  const {
    handleStepClick,
    handleImageButtonClick,
    handleVideoButtonClick,
    handleMediaUpload,
    handleVideoUpload,
    saveStepContent,
    removeMedia,
    handleDelete,
  } = useCourseDetailsHandlers(
    lngsltd,
    selectedStep,
    mediaInputRef,
    stepContentState,
    setSelectedStep,
    fetchCourseSteps,
    setStepContentState,
    isUploading,
    setIsUploading
  );

  // Load initial data after handlers are ready
  useEffect(() => {
    const loadInitialData = async () => {
      const steps = await fetchCourseSteps();
      // Load first step after handlers are ready
      if (steps?.length > 0 && !selectedStep && handleStepClick) {
        setTimeout(() => {
          handleStepClick(steps[0]);
        }, 100);
      }
    };
    loadInitialData();
  }, [courseId, fetchCourseSteps, selectedStep, handleStepClick]);


  if (!courseSteps) {
    return <div>{lngsltd["No Data"]}</div>;
  }

  return (
    <Box className='fullHW' px={{ xs: 2, sm: 3, md: 4, lg: 6 }} py={{ xs: 2, sm: 3, md: 4 }} display="flex" flexDirection="column" sx={{ bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <ToastContainer
        autoClose={2000} // Close after 2 seconds
        progressClassName='custom-toast-progress-bar'
      />

      {/* Header Section */}
      <Box 
        display="flex" 
        width="100%" 
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        flexDirection={{ xs: 'column', sm: 'row' }}
        gap={{ xs: 1.5, sm: 0 }}
        mb={2.5}
        sx={{
          pb: 1.5,
          borderBottom: '1px solid #e9ecef'
        }}
      >
        <Typography 
          variant="h5" 
          fontWeight={600}
          sx={{ 
            color: '#212529',
            fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.375rem' },
            letterSpacing: '-0.01em'
          }}
        >
          {lngsltd["Course Steps"]}
        </Typography>
        <Button
          variant="contained"
          size="medium"
          onClick={saveStepContent}
          disabled={isUploading}
          sx={{ 
            minWidth: { xs: '100%', sm: '120px' },
            height: { xs: '36px', sm: '38px' },
            backgroundColor: isUploading ? '#6c757d' : '#dc3545',
            borderRadius: '6px',
            fontWeight: 500,
            fontSize: '0.875rem',
            textTransform: 'none',
            boxShadow: isUploading 
              ? 'none'
              : '0 1px 3px rgba(220, 53, 69, 0.3)',
            position: 'relative',
            transition: 'all 0.2s ease',
            '&:hover:not(:disabled)': {
              backgroundColor: '#c82333',
              boxShadow: '0 2px 6px rgba(220, 53, 69, 0.4)'
            },
            '&:disabled': {
              opacity: 0.6
            }
          }}
        >
          {isUploading ? (
            <>
              <span style={{ opacity: 0.9 }}>Uploading...</span>
              <Box
                component="span"
                sx={{
                  position: 'absolute',
                  right: 12,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: 16,
                  height: 16,
                  border: '2px solid rgba(255,255,255,0.3)',
                  borderTopColor: 'white',
                  borderRadius: '50%',
                  animation: 'spin 0.8s linear infinite',
                  '@keyframes spin': {
                    '0%': { transform: 'translateY(-50%) rotate(0deg)' },
                    '100%': { transform: 'translateY(-50%) rotate(360deg)' }
                  }
                }}
              />
            </>
          ) : (
            lngsltd["Save"]
          )}
        </Button>
      </Box>


      <ContentBlock sx={{ 
        flexGrow: 1, 
        gap: { xs: 1.5, sm: 2, md: 3 }, 
        display: "flex", 
        flexDirection: { xs: "column", md: "row" }, 
        alignItems: "start", 
        py: { xs: 1.5, sm: 2 }, 
        "& p": { m: 0 } 
      }}>
        <LeftSideBlock
          lngsltd={lngsltd}
          courseSteps={courseSteps}
          selectedStep={selectedStep}
          handleStepClick={handleStepClick}
          handleEdit={handleEdit}
          handleDelete={handleDelete}
          handleShowAdd={handleShowAdd}
        />

        <RightSideBlock
          handleImageButtonClick={handleImageButtonClick}
          lngsltd={lngsltd}
          handleVideoButtonClick={handleVideoButtonClick}
          mediaInputRef={mediaInputRef}
          handleMediaUpload={handleMediaUpload}
          removeMedia={removeMedia}
          handleVideoUpload={handleVideoUpload}
          stepContentState={stepContentState}
          setStepContentState={setStepContentState}
          selectedStep={selectedStep}
        />

        <AddCourseStepModal
          showadd={showadd}
          handleCloseAdd={handleCloseAdd}
          lngsltd={lngsltd}
          newStepData={newStepData}
          handleStepNumberChange={handleStepNumberChange}
          stepNumberError={stepNumberError}
          setNewStepData={setNewStepData}
          handleSave={handleSave}
        />

        <UpdateCourseStepModal
          showedit={showedit}
          handleCloseEdit={handleCloseEdit}
          lngsltd={lngsltd}
          editStepTitle={editStepTitle}
          setEditStepTitle={setEditStepTitle}
          handleUpdate={handleUpdate}
        />
      </ContentBlock>
    </Box>
  );
};

export default CourseDetails;


// const [ContentType, setContentType] = useState("");
// const [englishDescriptionContent, setEnglishDescriptionContent] = useState('');
// const [norwegianDescriptionContent, setNorwegianDescriptionContent] = useState('');
// const [imageFiles, setImageFiles] = useState([]);
// const [imageUrls, setImageUrls] = useState([]);
// const [imageFileNames, setImageFileNames] = useState([]);
//  const [activeContentType, setActiveContentType] = useState(1);
// const [videoFile, setVideoFile] = useState([]);
// const [videoUrls, setVideoUrls] = useState([]);
// const [imageUrl, setImageUrl] = useState([]);
// const [videoUrl, setVideoUrl] = useState([]);
// imageUrls={imageUrls}
// activeContentType={activeContentType}
// videoUrls={videoUrls}
// englishDescriptionContent={englishDescriptionContent}
// setEnglishDescriptionContent={setEnglishDescriptionContent}
// norwegianDescriptionContent={norwegianDescriptionContent}
// setNorwegianDescriptionContent={setNorwegianDescriptionContent}
// setContentType,
//     activeDiv,
//     imageFileNames,
//     videoUrls,
//     englishDescriptionContent,
//     norwegianDescriptionContent,
//     ContentType,
//     imageFiles,
//     videoFile,
//     setActiveDiv,
//     setVideoUrls,
//     setImageUrls,
//     setImageFileNames,
//     setImageFiles,
//     setVideoFile,
//     setImageUrl,
//     setVideoUrl,
//     setEnglishDescriptionContent,
//     setNorwegianDescriptionContent